import { Component } from "react"
import PropTypes from "prop-types"
import { v4 as uuid } from "uuid"
import { connect } from "react-redux"
import { find, get, kebabCase } from "lodash"
import moment from "moment"
import classNames from "classnames"
import resizable from "hocs/resizable"
import tooltipContainer from "hocs/tooltip-container"
import { DATE, CHART_ITEM } from "helpers/shapes"
import Tooltip from "components/charts/tooltip"
import ChartDefs from "./components/chart-defs"
import { HIGHLIGHTER, LINE } from "../shapes"
import Dot from "../dot"
import { getData, getLine, getZero, getScaleX, getScaleY, getHighlighter, getSelected } from "./helper"
import "./style.scss"

class BandsLineChart extends Component {
  static propTypes = {
    tooltip: PropTypes.element.isRequired,
    line: LINE.isRequired,
    highlighter: HIGHLIGHTER,
    scaleX: PropTypes.func.isRequired,
    scaleY: PropTypes.func.isRequired,
    data: PropTypes.arrayOf(CHART_ITEM).isRequired,
    height: PropTypes.number.isRequired,
    width: PropTypes.number,
    resizableRef: PropTypes.func.isRequired,
    zero: PropTypes.number.isRequired,
    subscribe: PropTypes.func.isRequired,
    unsubscribe: PropTypes.func.isRequired,
    date: DATE,
    setDate: PropTypes.func.isRequired,
    label: PropTypes.string.isRequired,
    formatter: PropTypes.func.isRequired,
    selected: PropTypes.object,
  }

  constructor(props) {
    super(props)

    const chartId = uuid()
    this.pathGradientId = `path-gradient-${chartId}`
    this.axisGradientId = `axis-gradient-${chartId}`
    this.bandsGradientId = `bands-gradient-${chartId}`
    this.limitBandsGradientId = `limit-bands-gradient-${chartId}`
    this.valueElement = null
    this.labelElement = null
  }

  valueRef = el => {
    this.valueElement = el
  }

  labelRef = el => {
    this.labelElement = el
  }

  findByOffset = offset => {
    const { data, scaleX } = this.props
    const lookup = scaleX.invert(offset - 2) // -2 is to give a margin of selection on the first element
    return find(data, datum => datum.date >= lookup)
  }

  setDate = offset => {
    const { setDate } = this.props
    const { date } = this.findByOffset(offset)
    setDate(moment(date).format("YYYY-MM-DD"))
  }

  handleGraphClick = ev => {
    const box = ev.currentTarget.getBoundingClientRect()
    const offsetX = ev.pageX - box.left
    this.setDate(offsetX)
  }

  findDotCoordinates = offset => {
    const { scaleX, scaleY } = this.props
    const datum = this.findByOffset(offset)

    if (datum) {
      return {
        cx: scaleX(datum.date),
        cy: scaleY(datum.value),
      }
    } else {
      return null
    }
  }

  computeStyles = () => {
    const { line, highlighter, width } = this.props
    const pathStyle = {}
    const axisStyle = {}
    const bandsStyle = {}
    const limitBandsStyle = {}

    if (line.style.color && (!highlighter || !width)) {
      pathStyle.stroke = line.style.color
    } else if (highlighter && width) {
      pathStyle.stroke = `url(#${this.pathGradientId})`
    }

    return [pathStyle, axisStyle, bandsStyle, limitBandsStyle]
  }

  getValuePosition = () => {
    const { highlighter, width } = this.props
    let position = width

    if (highlighter) {
      // Put the `value` text label to the `right` of the `highlighter` by default.
      position = highlighter.x1 + 5

      if (this.valueElement) {
        const valueWidth = this.valueElement.getBoundingClientRect().width
        const labelWidth = this.labelElement.getBoundingClientRect().width

        /**
         * If the space available between the `right` of the chart and the `highlighter`
         * is < to the width of the `value` text label, then put the value to the left of the `highlighter`.
         *
         * Otherwise if the space available between the `left` of the chart and the `highlighter`
         * is < to the width of the `label` of the chart, then put the value to its `right`.
         */
        if ((width - highlighter.x1) < valueWidth) {
          position = (highlighter.x1 - valueWidth) - 5
        } else if (highlighter.x1 < labelWidth) {
          position = labelWidth + 5
        }
      }
    } else if (this.valueElement) {
      const valueWidth = this.valueElement.getBoundingClientRect().width
      position = width - valueWidth
    }

    return position
  }

  render() {
    const {
      data,
      resizableRef,
      height,
      line,
      zero,
      subscribe,
      unsubscribe,
      highlighter,
      tooltip,
      scaleY,
      label,
      formatter,
      selected,
      width,
    } = this.props

    if (!data.length) {
      return <div ref={resizableRef}>No Data</div>
    } else {
      const [pathStyle, axisStyle, bandsStyle, limitBandsStyle] = this.computeStyles()

      return <div data-test-class={`${kebabCase(label)}-chart`} className="components_charts_bands-line-chart">
        <div ref={this.labelRef} className="chart-title">{label}</div>
        <svg ref={resizableRef} className="chart" style={{ height }} onClick={this.handleGraphClick}>
          <ChartDefs pathId={this.pathGradientId} axisId={this.axisGradientId} bandsId={this.bandsGradientId} limitBandsId={this.limitBandsGradientId} offset={highlighter && highlighter.x1} chartWidth={width} />

          <line x1="0" x2="100%" y1={scaleY(2)} y2={scaleY(2)} className="limit-bands overbought-plus" style={limitBandsStyle} />
          <line x1="0" x2="100%" y1={scaleY(1)} y2={scaleY(1)} className="bands overbought" style={bandsStyle} />

          <line x1="0" y1={zero} y2={zero} x2="100%" className="axis" style={axisStyle} />

          <line x1="0" x2="100%" y1={scaleY(-1)} y2={scaleY(-1)} className="bands oversold" style={bandsStyle} />
          <line x1="0" x2="100%" y1={scaleY(-2)} y2={scaleY(-2)} className="limit-bands oversold-plus" style={limitBandsStyle} />

          <path className={classNames("path", { "single-dot": data && data.length === 1 })} {...line} style={pathStyle} />

          <g>
            {highlighter && <line className="highlighter" {...highlighter} />}
            <text ref={this.valueRef} y="-5" x={this.getValuePosition()} className="chart-value">
              {formatter(get(selected, "value")) || "N/A"} <tspan className={`label ${get(selected, "labelClassname")}`}>{get(selected, "label")}</tspan>
            </text>
          </g>
          <Dot {...{ subscribe, unsubscribe, findDotCoordinates: this.findDotCoordinates }} />
        </svg>
        <Tooltip {...{ subscribe, unsubscribe, findByOffset: this.findByOffset }}>
          {tooltip}
        </Tooltip>
      </div>
    }
  }
}

const mapStateToProps = (state, props) => ({
  data: getData(props),
  line: getLine(props),
  zero: getZero(props),
  scaleX: getScaleX(props),
  scaleY: getScaleY(props),
  highlighter: getHighlighter(props),
  selected: getSelected(props),
})

export default tooltipContainer(resizable(connect(mapStateToProps)(BandsLineChart)))
